import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import type { SmbHomeImageInspection } from "@shared/smb-home-images";

const counts = (data: SmbHomeImageInspection) => ({
  eligible: data.slots.filter(s => s.state === "eligible" || s.state === "conversion").length,
  preserved: data.slots.filter(s => s.state === "present").length,
  skipped: data.slots.filter(s => s.state === "supplied-only" || s.state === "conflict").length,
});

export function SmbHomeImageFill({ brandId, pageId, onBeforeFill }: {
  brandId: string; pageId: string; onBeforeFill?: () => Promise<boolean>;
}) {
  // Remount request/consent state when navigating between pages.
  return <HomeImageFill key={`${brandId}:${pageId}`} brandId={brandId} pageId={pageId} onBeforeFill={onBeforeFill} />;
}

function HomeImageFill({ brandId, pageId, onBeforeFill }: {
  brandId: string; pageId: string; onBeforeFill?: () => Promise<boolean>;
}) {
  const queryClient = useQueryClient();
  const base = `/api/brands/${brandId}/lp-express/smb-home/${pageId}/images`;
  const [confirmed, setConfirmed] = useState<SmbHomeImageInspection | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const guard = useRef(false);
  const identity = useRef<{ revision: string; id: string } | null>(null);
  const inspection = useQuery<SmbHomeImageInspection>({
    queryKey: [`${base}/inspection`],
    queryFn: async () => {
      const response = await fetch(`${base}/inspection`, { credentials: "include" });
      if (!response.ok) throw new Error("Could not check saved Home images. Try Fix all images again.");
      const data = await response.json();
      if (typeof data?.revision !== "string" || !Array.isArray(data?.slots))
        throw new Error("The saved Home image review was invalid.");
      return data;
    },
  });
  const fill = useMutation({
    mutationFn: async (vars: { requestIdentity: string; revision: string; allEligible: true; approveIllustration: true }) => {
      await apiRequest("POST", `${base}/fill`, vars);
    },
    onSuccess: async () => {
      setConfirmed(null);
      setMessage("Image repair submitted. Progress updates below; you can keep editing.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [`${base}/inspection`] }),
        queryClient.invalidateQueries({ queryKey: [base] }),
      ]);
    },
    onSettled: () => { guard.current = false; },
  });
  const begin = async () => {
    if (guard.current || confirmed) return;
    guard.current = true;
    setPreparing(true); setError(null); setMessage(null);
    try {
      if (onBeforeFill && !await onBeforeFill())
        throw new Error("Save or resolve the current Home page edits before fixing images.");
      const fresh = await inspection.refetch();
      if (fresh.error || !fresh.data) throw fresh.error || new Error("Could not review saved Home images.");
      const data = fresh.data;
      if (data.snapshotError || data.configurationError) throw new Error(data.snapshotError || data.configurationError);
      if (data.busy) throw new Error("An image or page operation needs to finish or be resolved before another repair.");
      const summary = counts(data);
      if (!summary.eligible) {
        setMessage(`Nothing can be filled. ${summary.preserved} existing images preserved; ${summary.skipped} upload-only, logo or conflicting positions skipped. No paid work started.`);
        return;
      }
      if (identity.current?.revision !== data.revision)
        identity.current = { revision: data.revision, id: crypto.randomUUID() };
      setConfirmed(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Page edits could not be saved or images reviewed. No image repair started.");
    } finally { setPreparing(false); guard.current = false; }
  };
  const submit = () => {
    if (!confirmed || guard.current || !identity.current) return;
    guard.current = true;
    fill.mutate({ requestIdentity: identity.current.id, revision: confirmed.revision,
      allEligible: true, approveIllustration: true });
  };
  const summary = confirmed ? counts(confirmed) : null;
  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label="Fix Home images" data-testid="smb-home-image-fill">
      <h3 className="font-medium">Home images</h3>
      <p className="text-sm text-muted-foreground">Fill all suitable missing images with labeled illustrations. Existing images and logos stay unchanged; documentary positions require uploads.</p>
      {(error || inspection.data?.configurationError || inspection.data?.snapshotError) && <p role="alert" className="text-sm text-destructive">{error || inspection.data?.configurationError || inspection.data?.snapshotError}</p>}
      {inspection.isError && !error && <p role="alert">Image review is unavailable. Try Fix all images again.</p>}
      {message && <p role="status" className="text-sm">{message}</p>}
      {inspection.data?.busy && <p role="status" className="text-sm">An image or page operation is in progress or needs attention.</p>}
      <Button type="button" onClick={() => void begin()} disabled={preparing || fill.isPending || !!confirmed || !!inspection.data?.busy}>
        {preparing ? "Saving and checking images…" : "Fix all images"}
      </Button>
      <Dialog open={!!confirmed} onOpenChange={open => { if (!open && !fill.isPending) setConfirmed(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fix all eligible images?</DialogTitle>
            <DialogDescription>
              Generate {summary?.eligible} illustrations through paid prompt writing and KIE image generation. This may incur charges for the entire batch.
              All generated images receive illustration alt text and the visible label “AI-generated illustration”; they are not photos or proof of this business.
              Existing images and logos remain unchanged. {summary?.skipped} upload-only or conflicting positions will be skipped, not repaired.
            </DialogDescription>
          </DialogHeader>
          {fill.isError && <p role="alert" className="text-sm text-destructive">{fill.error.message} Confirm again to check the same request. If the page changed, cancel and choose Fix all images again.</p>}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={fill.isPending} onClick={() => setConfirmed(null)}>Cancel</Button>
            <Button type="button" disabled={fill.isPending} onClick={submit}>{fill.isPending ? "Submitting…" : "Confirm and generate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
